import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { format } from "date-fns";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useAreaList,
  useBillCycleDetails,
  useBillCycleList,
  useBillTemplates,
  useBulkBillData,
  useCategoryConfigMap,
  useConsumerCount,
  usePremiseList,
  useSubAreaList,
  useSubCategoryConfigMap,
  useSubmitConsumerSelection,
  useUpdateConsumerSelection,
  useUtilityServices,
} from "../../hooks";
import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import { HoverRevealText } from "@shared/components/HoverRevealText";
interface TemplateOption {
  value: number;
  label: string;
  source: string;
  type?: string;
}
interface BillCycleStepProps {
  // Stepper-injected props
  onNext?: () => void;
  onPrevious?: () => void;
  currentStepIndex?: number;
  stepHelpers?: StepHelpers;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const BillCycleStep: React.FC<BillCycleStepProps> = ({
  onNext,
  onPrevious,
  currentStepIndex = 0,
  stepHelpers,
  setValidationError,
  clearValidationError,
  isValidating,
  isFirstStep,
  isLastStep,
}) => {
  const [createNew, setCreateNew] = useState(false);
  const [selectedBillCycleId, setSelectedBillCycleId] = useState<string>("");
  const [isNewCycle, setIsNewCycle] = useState(false);
  const { id, action = "add" } = useParams();

  const navigate = useNavigate();

  const { data: bulkBillData } = useBulkBillData(id);
  const { data: billCyclesList, isPending: billCycleListLoading } =
    useBillCycleList();
  const { data: utilityServices = [], isPending: isUtilityServiceLoading } =
    useActiveUtilityServices();
  const { data: categoryConfigMap = [], isPending: isCategoryLoading } =
    useCategoryConfigMap();
  const { data: areaList = [], isPending: isAreaLoading } = useAreaList();
  const params = useParams();

  const { data: billCycleDetails, isLoading: isBillCycleDetailsLoading } =
    useBillCycleDetails(selectedBillCycleId);

  // Create form instance first so we can watch values
  const [areaOptions, setAreaOptions] = useState<any[]>([]);
  const [subAreaOptions, setSubAreaOptions] = useState<any[]>([]);
  const [premiseOptions, setPremiseOptions] = useState<any[]>([]);
  const [utilityServiceOptions, setUtilityServiceOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<any[]>([]);
  // const [template, setTemplate] = useState<string>("");
  const [templateError, setTemplateError] = useState("");
  const [template, setTemplate] = useState<TemplateOption | null>(null);
  const dataLoadedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const billCycleId = searchParams.get("billCycleId");

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const billDraftId = getStepData()?.billDraftId || id;

  // Check if we're editing an existing bill cycle
  const getExistingBillCycleId = useCallback(() => {
    if (!stepHelpers) return null;

    // Check if there's a bill cycle ID from a previous run stored in step data
    const allStepData = stepHelpers.getAllStepData();

    // Look for bill cycle ID in current step or from a previous workflow completion
    for (const [stepIndex, data] of Object.entries(allStepData)) {
      if (data?.billCycleId || data?.selectedBillCycleId) {
        return data.billCycleId || data.selectedBillCycleId;
      }
    }

    return null;
  }, [stepHelpers]);

  // Create form with initial fields
  const formFields: FormField[] = [
    {
      name: "selectedCycle",
      label: "Enter Billing Cycle",
      type: "text",
      required: true,
      placeholder: "Enter a billing cycle name",
      className: "col-span-2",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 h-10 px-3",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      required: true,
      options: areaOptions,
      placeholder: isAreaLoading ? "loading areas..." : "Select areas",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "subAreas",
      label: "Sub Area",
      type: "multi-select",
      required: true,
      options: subAreaOptions,
      placeholder: "Select Sub Area",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "premises",
      label: "Premises",
      type: "multi-select",
      required: true,
      options: premiseOptions,
      placeholder: "Select premises",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "utilityService",
      label: "Utility Service",
      type: "multi-select",
      required: true,
      options: utilityServiceOptions,
      placeholder: isUtilityServiceLoading
        ? "loading utility service..."
        : "Select utility services",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "consumerCategory",
      label: "Consumer Category",
      type: "multi-select",
      required: true,
      options: categoryOptions,
      placeholder: isUtilityServiceLoading
        ? "loading categories..."
        : "Select consumer categories",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "consumerSubcategory",
      label: "Consumer Subcategory",
      type: "multi-select",
      required: true,
      options: subCategoryOptions,
      placeholder: "Select consumer subcategories",
      className: "col-span-2",
      searchable: true,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "billingPeriodStart",
      label: "Billing Period Start",
      type: "datepicker",
      required: true,
      placeholder: "Select start date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "billingPeriodEnd",
      label: "Billing Period End",
      type: "datepicker",
      required: true,
      placeholder: "Select end date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  useEffect(() => {
    if (params?.action == "edit") {
      handleBillCycleSelection(params?.id);
    }
  }, [navigate]);

  useEffect(() => {
    if (params?.action == "draft" && bulkBillData) {
      console.log(
        "bulkBillData.invoiceEndDate",
        bulkBillData.invoiceEndDate,
        bulkBillData.invoiceStartDate
      );
      handleBillCycleSelection(bulkBillData?.billScheduleData?.id);
      billCycleForm.setValue(
        "billingPeriodStart",
        dayjs(bulkBillData.billScheduleData?.invoiceStartDate).format(
          "YYYY-MM-DD"
        )
      );
      billCycleForm.setValue(
        "billingPeriodEnd",
        dayjs(bulkBillData.billScheduleData?.invoiceEndDate).format(
          "YYYY-MM-DD"
        )
      );
      const parse = JSON.stringify({
        value: bulkBillData?.billConfigId,
        label: bulkBillData?.billScheduleData?.billCycleName,
        source: bulkBillData?.billScheduleData?.billConfigType,
      });
      setTemplate(JSON.parse(parse));
      billCycleForm.setValue(
        "utilityService",
        bulkBillData?.billScheduleData?.utilityServices
      );
    }
  }, [navigate, bulkBillData]);

  const billCycleForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Watch form values for dynamic behavior
  const watchedValues = billCycleForm.watch();

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          // Populate form with saved data
          FormService.populateForm(billCycleForm, savedStepData, formFields);

          // Restore other state
          if (savedStepData.selectedBillCycleId) {
            setSelectedBillCycleId(savedStepData.selectedBillCycleId);
          }
          // if (savedStepData.template) {
          //   setTemplate(savedStepData.template);
          // }
          if (savedStepData.createNew !== undefined) {
            setCreateNew(savedStepData.createNew);
          }
          if (savedStepData.isNewCycle !== undefined) {
            setIsNewCycle(savedStepData.isNewCycle);
          }
        } else {
          // Check if we're editing an existing bill cycle from previous workflow
          const existingBillCycleId = getExistingBillCycleId();
          if (existingBillCycleId) {
            setSelectedBillCycleId(existingBillCycleId);
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        // dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, getStepData, getExistingBillCycleId, billCycleForm]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const currentData = getStepData() || {};
    const updatedData = {
      ...currentData,
      [fieldName]: value,
      lastUpdated: new Date().toISOString(),
    };
    saveStepData(updatedData);
  };

  // Get dependent data based on form values
  const { data: subAreaList = [], isPending: isSubAreaLoading } =
    useSubAreaList(
      watchedValues.area && Array.isArray(watchedValues.area)
        ? watchedValues.area
        : []
    );

  const { data: premiseList = [], isPending: isPremiseLoading } =
    usePremiseList(
      watchedValues.subAreas && Array.isArray(watchedValues.subAreas)
        ? watchedValues.subAreas
        : []
    );

  const { data: subCategoryList = [], isPending: isSubCategoryLoading } =
    useSubCategoryConfigMap(
      watchedValues.consumerCategory &&
        Array.isArray(watchedValues.consumerCategory)
        ? watchedValues.consumerCategory
        : []
    );

  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();

  const { mutateAsync: updateConsumer, isPending: isUpdating } =
    useUpdateConsumerSelection(getStepData()?.draftId || id);

  const { data: billTemplates } = useBillTemplates();

  const categoryLabels =
    watchedValues.consumerCategory &&
    Array.isArray(watchedValues.consumerCategory)
      ? watchedValues.consumerCategory
          .map(
            (categoryId: string) =>
              categoryConfigMap.find((cat: any) => cat.id === categoryId)?.id
          )
          .filter(Boolean)
      : [];

  const { data: consumerCount } = useConsumerCount(
    watchedValues.premises && Array.isArray(watchedValues.premises)
      ? watchedValues.premises
      : [],
    categoryLabels
  );

  // Update options when data changes
  useEffect(() => {
    if (areaList?.length > 0) {
      setAreaOptions(
        areaList.map((area: any) => ({
          value: area.id,
          label: area.name,
        }))
      );
    }
  }, [areaList]);

  useEffect(() => {
    if (subAreaList?.length > 0) {
      setSubAreaOptions(
        subAreaList.map((subArea: any) => ({
          value: subArea.id,
          label: subArea.name,
        }))
      );
    } else {
      setSubAreaOptions([]);
    }
  }, [subAreaList]);

  useEffect(() => {
    if (premiseList?.length > 0) {
      setPremiseOptions(
        premiseList.map((premise: any) => ({
          value: premise.id,
          label: premise.name,
        }))
      );
    } else {
      setPremiseOptions([]);
    }
  }, [premiseList]);

  useEffect(() => {
    if (utilityServices?.length > 0) {
      setUtilityServiceOptions(
        utilityServices.map((service: any) => ({
          value: service.id,
          label: service.name,
        }))
      );
    }
  }, [utilityServices]);

  useEffect(() => {
    if (categoryConfigMap?.length > 0) {
      setCategoryOptions(
        categoryConfigMap.map((category: any) => ({
          value: category.id,
          label: category.name,
        }))
      );
    }
  }, [categoryConfigMap]);

  useEffect(() => {
    if (subCategoryList?.length > 0) {
      setSubCategoryOptions(
        subCategoryList.map((subCategory: any) => ({
          value: subCategory.id,
          label: subCategory.name,
        }))
      );
    } else {
      setSubCategoryOptions([]);
    }
  }, [subCategoryList]);

  // Handle bill cycle selection and populate form data
  const handleBillCycleSelection = (cycleId: string) => {
    if (cycleId) {
      setSelectedBillCycleId(cycleId);
      setIsNewCycle(false);
      setCreateNew(false);
      billCycleForm.setValue("selectedCycle", cycleId);

      // Save selection to stepHelpers
      saveStepData({
        selectedBillCycleId: cycleId,
        isNewCycle: false,
        createNew: false,
        ...billCycleForm.getValues(),
      });
    }
  };

  useEffect(() => {
    if (billCycleId) {
      handleBillCycleSelection(billCycleId);
    }
  }, [navigate]);

  // Handle create new cycle
  const handleCreateNewToggle = () => {
    setCreateNew(!createNew);
    setIsNewCycle(true);
    setSelectedBillCycleId("");
    billCycleForm.reset();
    billCycleForm.setValue("selectedCycle", "");
  };

  // Populate form when bill cycle details are loaded
  useEffect(() => {
    if (billCycleDetails && selectedBillCycleId && !isNewCycle) {
      // Set basic fields first
      billCycleForm.setValue("selectedCycle", billCycleDetails.name);

      if (billCycleDetails.areas) {
        billCycleForm.setValue("area", billCycleDetails.areas);
      }

      if (billCycleDetails?.utilityServices?.length > 0) {
        const utilityServiceIds = billCycleDetails.utilityServices.map(
          (service: any) => service.name
        );

        billCycleForm.setValue("utilityService", utilityServiceIds);
      }

      if (billCycleDetails.consumerCategories) {
        billCycleForm.setValue(
          "consumerCategory",
          billCycleDetails.consumerCategories
        );
      }

      if (billCycleDetails.consumerSubCategories) {
        billCycleForm.setValue(
          "consumerSubcategory",
          billCycleDetails.consumerSubCategories
        );
      }

      // if (billCycleDetails.billConfiguaration && template == null) {
      //   const parsed = JSON.parse(billCycleDetails.billConfiguaration);
      //   setTemplate(parsed); // ✅ parsed is now a TemplateOption
      // }

      // Set dependent fields with delays to ensure API calls complete
      setTimeout(() => {
        if (billCycleDetails.subAreas) {
          billCycleForm.setValue("subAreas", billCycleDetails.subAreas);
        }

        setTimeout(() => {
          if (billCycleDetails.premise) {
            billCycleForm.setValue("premises", billCycleDetails.premise);
          }
        }, 300);
      }, 200);
    }
  }, [billCycleDetails, selectedBillCycleId, isNewCycle, billCycleForm]);

  // Handle form submission
  const handleFormSubmit = async (data: any) => {
    try {
      // Clear any previous validation errors
      if (clearValidationError) {
        clearValidationError();
      }

      const isValidForm = await FormService.validateForm(billCycleForm);
      const isTemplateSelected = !!template;

      if (!isTemplateSelected) {
        setTemplateError("Please select a bill template.");
      } else {
        setTemplateError("");
      }

      const isValid = isValidForm && isTemplateSelected;

      if (!isValid) {
        if (setValidationError) {
          setValidationError(
            "Please fill in all required fields and select a bill template."
          );
        }
        return;
      }

      let payload = FormService.createPayload(data, formFields);
      const formattedData = {
        bill_data: {
          select_cycle: selectedBillCycleId,
          bill_config_id: template.value,
          bill_config_type: template.source,
          remote_utility_id: String(getRemoteUtilityId()),
          bill_schedule_data: {
            id: selectedBillCycleId,
            bill_config_type: template.source,
            bill_cycle_name: payload.selectedCycle,
            area: payload.area,
            sub_area: payload.subAreas,
            premise: payload.premises,
            utility_services: payload.utilityService,
            category: payload.consumerCategory,
            sub_category: payload.consumerSubcategory,
            invoice_start_date: payload.billingPeriodStart,
            invoice_end_date: payload.billingPeriodEnd,
            consumer_count: consumerCount,
          },
        },
        step: {
          step_working_on: 0,
          total_steps: 5,
        },
      };

      let response;
      if (getStepData()?.draftId || id) {
        response = await updateConsumer(formattedData);
      } else {
        response = await submitConsumer(formattedData);
      }

      if (response) {
        const draftId = response?.billDraftId;

        // Save successful submission data to stepHelpers
        saveStepData({
          ...data,
          template,
          selectedBillCycleId,
          draftId: draftId, // This is the ID that will be passed to next steps
          formattedData,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        // Also save bill cycle ID to be accessible across steps
        if (stepHelpers) {
          stepHelpers.setDataToStep(
            currentStepIndex,
            "billCycleId",
            selectedBillCycleId
          );
          // Store it in a way that other steps can access it
          stepHelpers.setDataToStep(
            0,
            "workflowBillCycleId",
            selectedBillCycleId
          );
        }

        // Proceed to next step
        if (onNext) {
          onNext();
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (setValidationError) {
        setValidationError("Failed to submit form. Please try again.");
      }
    }
  };

  const handleDraft = async (data: any) => {
    try {
      // Clear any previous validation errors
      if (clearValidationError) {
        clearValidationError();
      }

      const isValidForm = await FormService.validateForm(billCycleForm);
      const isTemplateSelected = !!template;

      if (!isTemplateSelected) {
        setTemplateError("Please select a bill template.");
      } else {
        setTemplateError("");
      }

      const isValid = isValidForm && isTemplateSelected;

      if (!isValid) {
        if (setValidationError) {
          setValidationError(
            "Please fill in all required fields and select a bill template."
          );
        }
        return;
      }

      let payload = FormService.createPayload(data, formFields);
      const formattedData = {
        bill_data: {
          select_cycle: selectedBillCycleId,
          bill_config_id: template.value,
          remote_utility_id: String(getRemoteUtilityId()),
          bill_schedule_data: {
            id: selectedBillCycleId,
            bill_config_type: template.source,
            bill_cycle_name: payload.selectedCycle,
            area: payload.area,
            sub_area: payload.subAreas,
            premise: payload.premises,
            utility_services: payload.utilityService,
            category: payload.consumerCategory,
            sub_category: payload.consumerSubcategory,
            invoice_start_date: payload.billingPeriodStart,
            invoice_end_date: payload.billingPeriodEnd,
            consumer_count: consumerCount,
          },
        },
        step: {
          step_working_on: 0,
          total_steps: 5,
        },
      };

      let response;
      if (getStepData()?.draftId || id) {
        response = await updateConsumer(formattedData);
      } else {
        response = await submitConsumer(formattedData);
      }

      if (response) {
        const draftId = response?.billDraftId;

        // Save successful submission data to stepHelpers
        saveStepData({
          ...data,
          template,
          selectedBillCycleId,
          draftId: draftId, // This is the ID that will be passed to next steps
          formattedData,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        // Also save bill cycle ID to be accessible across steps
        if (stepHelpers) {
          stepHelpers.setDataToStep(
            currentStepIndex,
            "billCycleId",
            selectedBillCycleId
          );
          // Store it in a way that other steps can access it
          stepHelpers.setDataToStep(
            0,
            "workflowBillCycleId",
            selectedBillCycleId
          );
        }

        if (response) {
          navigate("/bx/history?tab=bulk&tab1=draft");
          await queryClient.refetchQueries({
            queryKey: [
              QueryKeyFactory.module.bx.billHistory.BulkBillDraft(),
              1,
            ],
            exact: false,
          });
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      if (setValidationError) {
        setValidationError("Failed to submit form. Please try again.");
      }
    }
  };

  const renderBillingCycleSummary = () => {
    const formValues = billCycleForm.getValues();

    const MultiNameDisplay = ({ selectedIds, list, placeholder = "—" }) => {
      if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
        return <div>{placeholder}</div>;
      }

      const names = selectedIds
        .map((id) => list.find((item) => item.id === id)?.name)
        .filter(Boolean) as string[];

      if (names.length === 0) {
        return <div>{placeholder}</div>;
      }

      const firstTwo = names.slice(0, 2).join(", ");
      const restCount = names.length - 2;

      const displayText =
        restCount > 0 ? `${firstTwo} + ${restCount} more` : firstTwo;

      return (
        <Popover>
          <PopoverTrigger asChild>
            <div className="cursor-pointer underline decoration-dotted">
              {displayText}
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-2 max-w-xs">
            <div className="space-y-1 text-sm">
              {names.map((name, idx) => (
                <div key={idx}>{name}</div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      );
    };
     const getNames = (ids: string[], list: any[]) =>
    ids
      ?.map((id) => list.find((item: any) => item.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "—";

   return (
    <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102 focus-within:shadow-2xl focus-within:ring-2 focus-within:ring-accent">
      <CardHeader className="pb-3">
        <CardTitle>Billing Cycle Summary</CardTitle>
        <CardDescription>
          Detailed overview of the selected billing cycle configuration.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-4">
            {/* Cycle Name */}
            <div>
              <span className="text-sm text-muted-foreground">Cycle Name</span>
              <div className="font-medium">{watchedValues.selectedCycle}</div>
            </div>

            {/* Areas */}
            <div>
              <span className="text-sm text-muted-foreground">Areas</span>
              <HoverRevealText text={getNames(formValues.area, areaList)} />
            </div>

            {/* Sub Areas */}
            <div>
              <span className="text-sm text-muted-foreground">Sub Areas</span>
              <HoverRevealText text={getNames(formValues.subAreas, subAreaList)} />
            </div>

            {/* Premises */}
            <div>
              <span className="text-sm text-muted-foreground">Premises</span>
              <HoverRevealText text={getNames(formValues.premises, premiseList)} />
            </div>

            {/* Utility Services */}
            <div>
              <span className="text-sm text-muted-foreground">Utility Services</span>
              <HoverRevealText
                text={getNames(formValues.utilityService, utilityServices)}
              />
            </div>

            {/* Consumer Categories */}
            <div>
              <span className="text-sm text-muted-foreground">Consumer Categories</span>
              <HoverRevealText
                text={getNames(formValues.consumerCategory, categoryConfigMap)}
              />
            </div>

            {/* Premises Count */}
            <div>
              <span className="text-sm text-muted-foreground">Premises Count</span>
              <div>
                {Array.isArray(formValues.premises)
                  ? formValues.premises.length
                  : 0}{" "}
                selected
              </div>
            </div>

            {/* Billing Period */}
            <div>
              <span className="text-sm text-muted-foreground">Billing Period</span>
              <div>
                {formValues.billingPeriodStart && formValues.billingPeriodEnd
                  ? `${format(
                      new Date(formValues.billingPeriodStart),
                      "yyyy-MM-dd"
                    )} to ${format(
                      new Date(formValues.billingPeriodEnd),
                      "yyyy-MM-dd"
                    )}`
                  : "—"}
              </div>
            </div>
          </div>

          {/* Bill Template */}
          <span className="text-sm text-muted-foreground">Bill Template</span>
          <Select
            value={template ? JSON.stringify(template) : ""}
            onValueChange={(value) => {
              setTemplate(JSON.parse(value));
              setTemplateError("");
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Bill Template" />
            </SelectTrigger>
            <SelectContent>
              {billTemplates?.map((option: any) => (
                <SelectItem
                  key={option.value}
                  value={JSON.stringify(option)}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {templateError && (
            <p className="text-sm text-red-500 mt-1">{templateError}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="page-subheading">
          Bill Cycle Configuration
        </h3>
        <p className="page-subtitle">
          Configure the billing cycle parameters and view summary or bill
          template.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102">
            <CardHeader className="pb-3">
              <CardTitle>Cycle Selection</CardTitle>
              <CardDescription>
                Select an existing billing cycle or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {isBillCycleDetailsLoading
                      ? "loading data..."
                      : "Select Billing Cycle"}
                  </label>
                  <select
                    value={selectedBillCycleId}
                    onChange={(e) => handleBillCycleSelection(e.target.value)}
                    disabled={createNew}
                    className="w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 h-10 px-3"
                  >
                    <option value="">
                      {billCycleListLoading
                        ? "loading billing cycle..."
                        : "Select a billing cycle"}
                    </option>
                    {billCyclesList?.map((cycle: any) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant={createNew ? "default" : "outline"}
                    size="sm"
                    className="mt-2"
                    onClick={handleCreateNewToggle}
                  >
                    {createNew ? "Creating New" : "Create New"}
                  </Button>
                  {selectedBillCycleId && (
                    <div className="flex flex-col space-y-3">
                      <div className="text-sm font-medium text-gray-600 mt-4">
                        Cycle Comparison
                      </div>
                      <div className="flex justify-between items-start text-sm text-gray-600">
                        <div>
                          <div className="mb-1">Previous Period</div>
                          <div className="text-gray-900">
                            {billCycleDetails?.invoiceStartDate ?? "NA"} to{" "}
                            {billCycleDetails?.invoiceEndDate ?? "NA"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="mb-1">Days Since Last Billing</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {billCycleDetails?.daysSinceLastReading ?? "NA"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {
            <Card className="transition-all duration-300 hover:shadow-xl hover:scale-102">
              <CardHeader className="pb-3">
                <CardTitle>Cycle Configuration</CardTitle>
                <CardDescription>
                  Configure your billing cycle parameters
                </CardDescription>
              </CardHeader>
              {
                <CardContent>
                  <DynamicForm
                    fields={formFields}
                    form={billCycleForm}
                    onSubmit={handleFormSubmit}
                    onFieldChange={handleFieldChange}
                    gridColumns={2}
                    responsiveColumns={{ sm: 1, md: 2 }}
                    config={{
                      layout: "vertical",
                      spacing: "small",
                      showSubmitButton: false,
                      showResetButton: false,
                      classes: {
                        form: "space-y-4",
                        fieldContainer: "w-full",
                      },
                    }}
                  />
                </CardContent>
              }
            </Card>
          }
        </div>

        <div className="space-y-4">
          {(selectedBillCycleId || createNew) && (
            <>
              {renderBillingCycleSummary()}
              {watchedValues.billTemplate}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button onClick={() => handleDraft(billCycleForm.getValues())}>
          {" "}
          {isSubmitting || isUpdating ? (
            <div className="flex items-center">
              <span className="mr-2">Saving...</span>
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            </div>
          ) : (
            "Save As Draft"
          )}
        </Button>
        <Button onClick={() => handleFormSubmit(billCycleForm.getValues())}>
          {" "}
          {isSubmitting || isUpdating ? (
            <div className="flex items-center">
              <span className="mr-2">Processing</span>
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
            </div>
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default BillCycleStep;
